// "Subir a Mercado Libre": manda una foto generada a una publicación. Elegís la publicación
// (preseleccionada si el producto de la foto vino de Mercado Libre), el color si tiene, y si va
// de portada o secundaria. La portada se revisa con Mercado Libre antes de subir; si no pasa,
// se ofrece secundaria. Todo lo hace atlas-backend: acá sólo se pide y se sigue el job
// (ADR-2026-09-28-mercadolibre-subir-fotos-sin-borrar-nada, en atlas-backend).

import React from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Check, Loader2, Package, Search, X } from "lucide-react";
import { ErrorDeMercadoLibre } from "../lib/mercadoLibreConexionLogica";
import { listarPublicacionesDeMl } from "../lib/mercadoLibreCatalogo";
import type { PublicacionDeMl } from "../lib/mercadoLibreCatalogoLogica";
import {
  describirPublicacionDeMl,
  diagnosticarFotoEnMl,
  esperarSubidaAMl,
  subirFotoAMl,
  sugerenciaDeDestino,
} from "../lib/mercadoLibreFotos";
import {
  hayLugar,
  resumenDeSubida,
  type DiagnosticoDeFoto,
  type PublicacionParaSubir,
} from "../lib/mercadoLibreFotosLogica";
import { MercadoLibreIcon } from "./MercadoLibreIcon";

interface Props {
  open: boolean;
  /** La foto a subir. */
  imagen: { id: string; varianteId?: string | null } | null;
  brandId: string | null;
  onClose: () => void;
  zIndex?: number;
}

type Fase = "cargando" | "elegir" | "subiendo" | "listo" | "error";
const PIDE_CONECTAR = new Set(["sin_conexion", "reconectar"]);

export const SubirAMercadoLibreModal: React.FC<Props> = ({ open, imagen, brandId, onClose, zIndex = 120 }) => {
  const [fase, setFase] = React.useState<Fase>("cargando");
  const [lista, setLista] = React.useState<PublicacionDeMl[]>([]);
  const [busqueda, setBusqueda] = React.useState("");
  const [itemId, setItemId] = React.useState<string | null>(null);
  const [detalle, setDetalle] = React.useState<PublicacionParaSubir | null>(null);
  const [cargandoDetalle, setCargandoDetalle] = React.useState(false);
  const [variationId, setVariationId] = React.useState<string | null>(null);
  const [posicion, setPosicion] = React.useState<"secundaria" | "portada">("secundaria");
  const [diagnostico, setDiagnostico] = React.useState<DiagnosticoDeFoto | null>(null);
  const [diagnosticando, setDiagnosticando] = React.useState(false);
  const [forzar, setForzar] = React.useState(false);
  const [error, setError] = React.useState<{ code: string; message: string } | null>(null);
  const [resultado, setResultado] = React.useState<{ texto: string; ok: boolean } | null>(null);
  const sugeridaRef = React.useRef<string | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);

  const mostrarError = (e: unknown) => {
    const code = e instanceof ErrorDeMercadoLibre ? e.code : "error";
    setError({ code, message: e instanceof Error ? e.message : "Algo salió mal." });
  };

  // Al abrir: la lista de publicaciones y la sugerencia de destino, en paralelo.
  React.useEffect(() => {
    if (!open || !imagen) return;
    let cancelado = false;
    setFase("cargando");
    setError(null);
    setResultado(null);
    setItemId(null);
    setDetalle(null);
    setVariationId(null);
    setPosicion("secundaria");
    setDiagnostico(null);
    setForzar(false);
    setBusqueda("");
    sugeridaRef.current = null;
    Promise.all([
      listarPublicacionesDeMl(brandId, "active", false),
      imagen.varianteId ? sugerenciaDeDestino(brandId, imagen.varianteId) : Promise.resolve({ item_id: null, variation_id: null }),
    ])
      .then(([l, s]) => {
        if (cancelado) return;
        setLista(l.publicaciones);
        setFase("elegir");
        if (s.item_id) {
          sugeridaRef.current = s.variation_id;
          setItemId(s.item_id);
        }
      })
      .catch((e) => {
        if (cancelado) return;
        mostrarError(e);
        setFase("error");
      });
    return () => {
      cancelado = true;
    };
  }, [open, imagen, brandId]);

  // Al elegir publicación: sus colores, fotos y bloqueos.
  React.useEffect(() => {
    if (!open || !itemId) return;
    let cancelado = false;
    setCargandoDetalle(true);
    setDetalle(null);
    setDiagnostico(null);
    setError(null);
    describirPublicacionDeMl(brandId, itemId)
      .then((d) => {
        if (cancelado) return;
        setDetalle(d);
        const sugerida = sugeridaRef.current;
        const valida = d.colores.find((c) => c.variation_id === sugerida);
        setVariationId(valida ? valida.variation_id : d.colores.length === 1 ? d.colores[0]!.variation_id : null);
      })
      .catch((e) => {
        if (!cancelado) mostrarError(e);
      })
      .finally(() => {
        if (!cancelado) setCargandoDetalle(false);
      });
    return () => {
      cancelado = true;
    };
  }, [open, itemId, brandId]);

  // La portada se revisa con Mercado Libre apenas se elige.
  React.useEffect(() => {
    if (!open || !imagen || !itemId || posicion !== "portada" || !detalle || detalle.bloqueo) return;
    let cancelado = false;
    setDiagnosticando(true);
    setDiagnostico(null);
    setForzar(false);
    diagnosticarFotoEnMl(brandId, { itemId, imagenId: imagen.id, posicion: "portada" })
      .then((d) => {
        if (!cancelado) setDiagnostico(d);
      })
      .catch(() => {
        if (!cancelado) setDiagnostico({ apta: true, motivos: [], sin_diagnostico: true });
      })
      .finally(() => {
        if (!cancelado) setDiagnosticando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [open, imagen, itemId, posicion, detalle, brandId]);

  React.useEffect(() => {
    if (!open) abortRef.current?.abort();
  }, [open]);

  if (!open || !imagen) return null;

  const filtrada = busqueda.trim()
    ? lista.filter((p) => p.titulo.toLowerCase().includes(busqueda.trim().toLowerCase()))
    : lista;
  const faltaColor = !!detalle && detalle.colores.length > 1 && !variationId;
  const sinLugar = !!detalle && !hayLugar(detalle);
  const portadaDudosa = posicion === "portada" && !!diagnostico && (!diagnostico.apta || diagnostico.sin_diagnostico);
  const puedeSubir =
    !!detalle && !detalle.bloqueo && !faltaColor && !sinLugar && !cargandoDetalle && !(posicion === "portada" && diagnosticando);

  const subir = async () => {
    if (!puedeSubir || !itemId) return;
    setFase("subiendo");
    setError(null);
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const id = await subirFotoAMl(brandId, {
        itemId,
        imagenId: imagen.id,
        posicion,
        variationId: detalle && detalle.colores.length > 0 ? variationId : null,
        forzarPortada: posicion === "portada" && forzar,
      });
      // null = esa foto ya estaba subida (o subiéndose) a esta publicación: no se sube dos veces.
      const r = id ? await esperarSubidaAMl(id, ac.signal) : null;
      if (ac.signal.aborted) return;
      setResultado(id ? resumenDeSubida(r) : { texto: "Esa foto ya está en la publicación (o se está subiendo).", ok: true });
      setFase("listo");
    } catch (e) {
      if (ac.signal.aborted) return;
      mostrarError(e);
      setFase("error");
    }
  };

  const irAConectar = () => {
    onClose();
    window.location.assign("/mi-marca?conectar=mercadolibre");
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex }}>
      <div className="bg-white rounded-2xl shadow-xl w-[min(520px,calc(100vw-32px))] max-h-[85vh] flex flex-col overflow-hidden">
        <div className="px-6 pt-6 pb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[20px] font-semibold text-brand-black/90 inline-flex items-center gap-2">
              <MercadoLibreIcon className="h-5 w-5" />
              Subir a Mercado Libre
            </h2>
            <p className="text-[13px] text-brand-black/50 mt-1">
              Elegí la publicación. La foto se suma a las que ya tiene: no se borra ninguna.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-3 space-y-4">
          {fase === "cargando" && (
            <div className="flex flex-col items-center justify-center py-14 text-brand-black/45">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <p className="text-[14px]">Leyendo tus publicaciones…</p>
            </div>
          )}

          {fase === "error" && error && (
            <div className="flex flex-col items-center justify-center text-center py-10 gap-3">
              <p className="text-[14px] text-brand-black/70 max-w-sm">{error.message}</p>
              {PIDE_CONECTAR.has(error.code) && (
                <button
                  type="button"
                  onClick={irAConectar}
                  className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white font-medium text-[length:var(--text-body)] shadow-sm"
                >
                  {error.code === "reconectar" ? "Reconectar Mercado Libre" : "Conectar Mercado Libre"}
                </button>
              )}
            </div>
          )}

          {fase === "elegir" && (
            <>
              {/* 1. Publicación */}
              <div>
                <p className="text-[12.5px] font-medium text-brand-black/50 mb-1.5">Publicación</p>
                {lista.length === 0 ? (
                  <p className="text-[13.5px] text-brand-black/55">No tenés publicaciones activas en Mercado Libre.</p>
                ) : (
                  <>
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-black/30" />
                      <input
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        placeholder="Buscar por nombre"
                        className="w-full h-9 pl-8 pr-3 rounded-md border border-neutral-200 text-[13.5px] focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none"
                      />
                    </div>
                    <div className="max-h-[220px] overflow-y-auto space-y-1 pr-1">
                      {filtrada.map((p) => {
                        const id = p.item_ids[0]!;
                        const elegida = id === itemId;
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => {
                              sugeridaRef.current = null;
                              setItemId(id);
                            }}
                            className={`w-full flex items-center gap-2.5 rounded-lg border px-2 py-1.5 text-left transition-colors ${
                              elegida ? "border-brand-primary bg-brand-primary/5" : "border-neutral-200 hover:bg-neutral-50"
                            }`}
                          >
                            <span className="w-9 h-9 rounded-md overflow-hidden bg-neutral-100 shrink-0 flex items-center justify-center">
                              {p.miniatura ? (
                                <img src={p.miniatura} alt="" className="w-full h-full object-cover" loading="lazy" />
                              ) : (
                                <Package className="w-4 h-4 text-brand-black/25" />
                              )}
                            </span>
                            <span className="flex-1 min-w-0 text-[13.5px] text-brand-black/80 truncate">{p.titulo}</span>
                            {p.es_catalogo && <span className="text-[11px] text-brand-black/40 shrink-0">Catálogo</span>}
                            {elegida && <Check className="w-4 h-4 text-brand-primary shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {cargandoDetalle && (
                <p className="text-[13px] text-brand-black/45 inline-flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Mirando la publicación…
                </p>
              )}
              {error && fase === "elegir" && <p className="text-[13px] text-error">{error.message}</p>}

              {detalle && !cargandoDetalle && (
                <>
                  {detalle.bloqueo ? (
                    <p className="text-[13.5px] text-amber-700">{detalle.bloqueo}</p>
                  ) : (
                    <>
                      {/* 2. Color, si la publicación tiene variantes */}
                      {detalle.colores.length > 1 && (
                        <div>
                          <p className="text-[12.5px] font-medium text-brand-black/50 mb-1.5">¿A qué variante va la foto?</p>
                          <div className="flex flex-wrap gap-1.5">
                            {detalle.colores.map((c) => (
                              <button
                                key={c.variation_id}
                                type="button"
                                onClick={() => setVariationId(c.variation_id)}
                                className={`h-8 px-3 rounded-full border text-[13px] transition-colors ${
                                  variationId === c.variation_id
                                    ? "border-brand-primary bg-brand-primary/10 text-brand-black/85"
                                    : "border-neutral-200 text-brand-black/65 hover:bg-neutral-50"
                                }`}
                              >
                                {c.etiqueta}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 3. Portada o secundaria */}
                      <div>
                        <p className="text-[12.5px] font-medium text-brand-black/50 mb-1.5">Lugar de la foto</p>
                        <div className="inline-flex rounded-lg bg-black/[0.05] p-0.5 text-[13px]">
                          {(["secundaria", "portada"] as const).map((x) => (
                            <button
                              key={x}
                              type="button"
                              onClick={() => setPosicion(x)}
                              className={`px-3 h-8 rounded-md font-medium transition-colors ${
                                posicion === x ? "bg-white shadow-sm text-brand-black/85" : "text-brand-black/50"
                              }`}
                            >
                              {x === "secundaria" ? "Sumar a las fotos" : "Usar de portada"}
                            </button>
                          ))}
                        </div>
                        {posicion === "portada" && diagnosticando && (
                          <p className="mt-2 text-[13px] text-brand-black/45 inline-flex items-center gap-1.5">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Revisando con Mercado Libre…
                          </p>
                        )}
                        {posicion === "portada" && diagnostico && diagnostico.apta && !diagnostico.sin_diagnostico && (
                          <p className="mt-2 text-[13px] text-emerald-700 inline-flex items-center gap-1.5">
                            <Check className="w-3.5 h-3.5" /> Mercado Libre la acepta como portada.
                          </p>
                        )}
                        {portadaDudosa && (
                          <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[13px] text-amber-800 space-y-1.5">
                            <p className="inline-flex items-start gap-1.5">
                              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                              <span>
                                {diagnostico!.sin_diagnostico
                                  ? "No pudimos confirmar con Mercado Libre que sirva de portada. Si seguís, la sumamos como foto secundaria."
                                  : `${diagnostico!.motivos.join(" ")} Si seguís, la sumamos como foto secundaria.`}
                              </span>
                            </p>
                            {!diagnostico!.sin_diagnostico && (
                              <label className="flex items-center gap-2 text-amber-900">
                                <input type="checkbox" checked={forzar} onChange={(e) => setForzar(e.target.checked)} />
                                Usarla de portada igual
                              </label>
                            )}
                          </div>
                        )}
                      </div>

                      {sinLugar && (
                        <p className="text-[13px] text-amber-700">
                          La publicación ya tiene {detalle.fotos} fotos, el máximo de Mercado Libre. Sacá alguna allá primero.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {fase === "subiendo" && (
            <div className="flex flex-col items-center justify-center text-center py-14 text-brand-black/55 gap-2">
              <Loader2 className="w-6 h-6 animate-spin" />
              <p className="text-[14px]">Subiendo la foto…</p>
              <p className="text-[12.5px] text-brand-black/40 max-w-xs">Si cerrás esta ventana, se sigue subiendo igual.</p>
            </div>
          )}

          {fase === "listo" && resultado && (
            <div className="flex flex-col items-center justify-center text-center py-10 gap-2">
              <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center">
                {resultado.ok ? <Check className="w-5 h-5 text-brand-primary" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
              </div>
              <p className={`text-[14px] max-w-sm ${resultado.ok ? "text-brand-black/70" : "text-amber-700"}`}>{resultado.texto}</p>
              {resultado.ok && detalle?.permalink && (
                <a href={detalle.permalink} target="_blank" rel="noreferrer" className="text-[13px] text-brand-primary underline underline-offset-2">
                  Ver la publicación
                </a>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 flex items-center justify-end gap-3 border-t border-neutral-100">
          {fase === "elegir" ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="h-[44px] px-3 text-[14px] font-medium text-brand-black/55 hover:text-brand-black/90 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={subir}
                disabled={!puedeSubir}
                className="inline-flex items-center justify-center gap-2 h-[44px] px-6 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Subir foto
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center h-[44px] px-6 rounded-md border border-neutral-200 text-neutral-900 bg-surface-card hover:bg-neutral-100 text-[15px] font-medium shadow-sm transition-colors"
            >
              {fase === "subiendo" ? "Seguir en segundo plano" : "Cerrar"}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SubirAMercadoLibreModal;
